import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';

class ThreeJSViewer {
  constructor(canvas, container) {
    this.canvas = canvas;
    this.container = container;
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.controls = null;
    this.model = null;
    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();
    this.hotspots = [];
    this.hoveredHotspot = null;
    this.onComponentHover = null;
    this.animationId = null;

    this.init();
  }

  init() {
    // Scene
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xfffdfb); // Match card color

    // Camera
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;

    this.camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    this.camera.position.set(3, 1, 3);

    // Renderer
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: true
    });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.2;

    // Lighting setup for clean, refined look
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    this.scene.add(ambientLight);

    // Key light (main light from top-front)
    const keyLight = new THREE.DirectionalLight(0xffffff, 1.0);
    keyLight.position.set(5, 10, 5);
    keyLight.castShadow = true;
    keyLight.shadow.camera.near = 0.1;
    keyLight.shadow.camera.far = 50;
    keyLight.shadow.camera.left = -10;
    keyLight.shadow.camera.right = 10;
    keyLight.shadow.camera.top = 10;
    keyLight.shadow.camera.bottom = -10;
    keyLight.shadow.mapSize.width = 2048;
    keyLight.shadow.mapSize.height = 2048;
    this.scene.add(keyLight);

    // Fill light (softer from the side)
    const fillLight = new THREE.DirectionalLight(0xffffff, 0.4);
    fillLight.position.set(-5, 3, -5);
    this.scene.add(fillLight);

    // Rim light (from behind for definition)
    const rimLight = new THREE.DirectionalLight(0xffffff, 0.3);
    rimLight.position.set(0, 3, -10);
    this.scene.add(rimLight);

    // Subtle hemisphere light for natural look
    const hemiLight = new THREE.HemisphereLight(0xffffff, 0xf5f3f0, 0.3);
    this.scene.add(hemiLight);

    // Controls
    this.controls = new OrbitControls(this.camera, this.canvas);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.minDistance = 3;
    this.controls.maxDistance = 20;
    this.controls.maxPolarAngle = Math.PI / 1.5;
    this.controls.target.set(0, 0, 0);

    // Event listeners
    this.canvas.addEventListener('mousemove', this.onMouseMove.bind(this), false);
    window.addEventListener('resize', this.onWindowResize.bind(this), false);

    // Start animation loop
    this.animate();
  }

  loadModel(modelPath, components, onComponentHover, debugMode = false) {
    this.onComponentHover = onComponentHover;
    this.debugMode = debugMode;

    const loader = new GLTFLoader();

    return new Promise((resolve, reject) => {
      loader.load(
        modelPath,
        (gltf) => {
          this.model = gltf.scene;

          // Enhance materials for clean look
          this.model.traverse((child) => {
            if (child.isMesh) {
              if (child.material) {
                child.material.metalness = 0.3;
                child.material.roughness = 0.6;

                // Make materials slightly lighter for the refined aesthetic
                if (child.material.color) {
                  const color = child.material.color;
                  color.r = Math.min(1, color.r * 1.1);
                  color.g = Math.min(1, color.g * 1.1);
                  color.b = Math.min(1, color.b * 1.1);
                }
              }

              child.castShadow = true;
              child.receiveShadow = true;
            }
          });

          // Center and scale the model
          const box = new THREE.Box3().setFromObject(this.model);
          const center = box.getCenter(new THREE.Vector3());
          const size = box.getSize(new THREE.Vector3());

          // Scale model to fit nicely
          const maxDim = Math.max(size.x, size.y, size.z);
          const scale = 4 / maxDim;
          this.model.scale.multiplyScalar(scale);

          // Center the model
          box.setFromObject(this.model);
          box.getCenter(center);
          this.model.position.sub(center);

          this.scene.add(this.model);

          // Create interactive hotspots
          this.createHotspots(components);

          resolve();
        },
        (xhr) => {
          const percentComplete = (xhr.loaded / xhr.total) * 100;
          console.log('Model ' + Math.round(percentComplete) + '% loaded');
        },
        (error) => {
          console.error('Error loading model:', error);
          reject(error);
        }
      );
    });
  }

  createHotspots(components) {
    if (!this.model || !components) return;

    // Get model bounding box for positioning
    const box = new THREE.Box3().setFromObject(this.model);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());

    // Log bounding box info for debugging
    console.log('─────────────────────────────────────────');
    console.log('📦 MODEL BOUNDING BOX INFO:');
    console.log(`   Center: (${center.x.toFixed(2)}, ${center.y.toFixed(2)}, ${center.z.toFixed(2)})`);
    console.log(`   Size:   (${size.x.toFixed(2)}, ${size.y.toFixed(2)}, ${size.z.toFixed(2)})`);
    console.log(`   Min:    (${box.min.x.toFixed(2)}, ${box.min.y.toFixed(2)}, ${box.min.z.toFixed(2)})`);
    console.log(`   Max:    (${box.max.x.toFixed(2)}, ${box.max.y.toFixed(2)}, ${box.max.z.toFixed(2)})`);
    console.log('─────────────────────────────────────────');

    // Hotspot geometry
    const geometry = new THREE.SphereGeometry(0.15, 16, 16);

    // Colors for debug mode (different color per component type)
    const debugColors = [0xff0000, 0x00ff00, 0x0000ff, 0xffff00, 0xff00ff, 0x00ffff];
    let colorIndex = 0;

    components.forEach(component => {
      const debugColor = debugColors[colorIndex % debugColors.length];
      colorIndex++;

      component.hotspots.forEach((hotspotPos, index) => {
        // Create material (visible in debug mode, invisible otherwise)
        const material = new THREE.MeshBasicMaterial({
          color: debugColor,
          transparent: true,
          opacity: this.debugMode ? 0.5 : 0,
          visible: true
        });

        const hotspot = new THREE.Mesh(geometry, material);

        // Position hotspot based on mode
        let x, y, z;
        const mode = hotspotPos.mode || 'relative'; // Default to relative

        if (mode === 'absolute') {
          // Absolute world coordinates (easier to tune)
          x = hotspotPos.x;
          y = hotspotPos.y;
          z = hotspotPos.z;
        } else {
          // Relative to bounding box (percentage-based)
          x = size.x * hotspotPos.x;
          y = size.y * hotspotPos.y;
          z = size.z * hotspotPos.z;
        }

        hotspot.position.set(x, y, z);
        hotspot.userData.component = component.key;
        hotspot.userData.componentData = component;
        hotspot.userData.isHotspot = true;

        this.scene.add(hotspot);
        this.hotspots.push(hotspot);

        // Log hotspot position in debug mode
        if (this.debugMode) {
          console.log(`🎯 ${component.name} [${index + 1}]: (${x.toFixed(2)}, ${y.toFixed(2)}, ${z.toFixed(2)}) [${mode}]`);
        }
      });
    });

    console.log(`✅ Created ${this.hotspots.length} interactive hotspots`);
    if (this.debugMode) {
      console.log('🔍 DEBUG MODE: Hotspots are visible as colored spheres');
      console.log('─────────────────────────────────────────');
    }
  }

  onMouseMove(event) {
    if (this.hotspots.length === 0) return;

    const rect = this.canvas.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(this.mouse, this.camera);

    // Check for hotspot intersections
    const intersects = this.raycaster.intersectObjects(this.hotspots, false);

    // Reset previous hovered hotspot
    if (this.hoveredHotspot && (!intersects.length || intersects[0].object !== this.hoveredHotspot)) {
      this.hoveredHotspot = null;
    }

    if (intersects.length > 0) {
      const hotspot = intersects[0].object;

      if (hotspot.userData.isHotspot) {
        if (this.hoveredHotspot !== hotspot) {
          this.hoveredHotspot = hotspot;

          // Notify React component of component hover
          if (this.onComponentHover) {
            this.onComponentHover(hotspot.userData.componentData);
          }
        }

        this.canvas.style.cursor = 'pointer';
      }
    } else {
      this.canvas.style.cursor = 'grab';
    }
  }

  onWindowResize() {
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }

  animate() {
    this.animationId = requestAnimationFrame(this.animate.bind(this));
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  }

  dispose() {
    // Cleanup
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }

    this.canvas.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('resize', this.onWindowResize);

    if (this.renderer) {
      this.renderer.dispose();
    }

    if (this.controls) {
      this.controls.dispose();
    }

    // Dispose of geometries and materials
    this.hotspots.forEach(hotspot => {
      hotspot.geometry.dispose();
      hotspot.material.dispose();
    });

    if (this.model) {
      this.model.traverse((child) => {
        if (child.geometry) {
          child.geometry.dispose();
        }
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(material => material.dispose());
          } else {
            child.material.dispose();
          }
        }
      });
    }
  }
}

export default ThreeJSViewer;
